import { ChangeEvent, useCallback, useEffect } from "react"

import { Form } from "react-bootstrap"

import DrugService, { IDrugService } from "../../../api/Administration/DrugService"
import AccountService from "../../../api/Administration/Model/AccountService"
import Service from "../../../api/Administration/Model/Service"
import { ServiceTests } from "../../../api/Administration/Model/ServiceTests"
import TestToBePerform from "../../../api/LabService/LabServiceModel/TestToBePerform"
import { ServiceCode } from "../../../api/LookupCodes/ServiceCode"
import { DOTSL_PANEL_CODES, DOTU_PANEL_CODES } from "../../../config/Constants"
import { useStateWithCallback } from "../../../utils/useStateWithCallback"

interface DrugServicesPanelProps {
  orderableServices?: Service[]
  orderableAccountServices?: AccountService[]
  selectedServiceCode?: ServiceCode
  canAddDOT: boolean
  canAddNonDOT: boolean
  canAddPOCT: boolean
  canAddFLEX: boolean
  serviceTests?: ServiceTests
  notMarketplaceServicesCount: number
  marketplaceServicesCount: number
  panelId?: number
  onChangeTestType: (value: string) => void
  onUpdateTests?: (tests: TestToBePerform[]) => void
  setAllServicesDisabled: (disabled: boolean) => void
}

interface ServiceWithOptions extends IDrugService {
  disabled: boolean
  checked: boolean
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}

function DrugServicesPanel({
  orderableServices = [],
  orderableAccountServices = [],
  selectedServiceCode = undefined,
  canAddDOT,
  canAddNonDOT,
  canAddPOCT,
  canAddFLEX,
  serviceTests = {} as ServiceTests,
  notMarketplaceServicesCount,
  marketplaceServicesCount,
  panelId = undefined,
  onChangeTestType,
  onUpdateTests = () => {},
  setAllServicesDisabled,
}: DrugServicesPanelProps) {
  const [services, setServices] = useStateWithCallback<ServiceWithOptions[]>([])

  const canAddTests = useCallback(
    (service: IDrugService) => {
      if (service.isDOT) {
        return canAddDOT
      }
      if (service.isNonDOTSaliva || service.isNonDOTUrine) {
        return canAddNonDOT
      }
      if (service.isPOCT) {
        return canAddPOCT
      }
      if (service.isFlex) {
        return canAddFLEX
      }
      return true
    },
    [canAddDOT, canAddFLEX, canAddNonDOT, canAddPOCT]
  )

  const disabledRegulatedSalivaService = useCallback(
    (service: IDrugService) => {
      const isSalivaPanelsAvailable = serviceTests?.[ServiceCode.DrugDOTOral].some((t) =>
        DOTSL_PANEL_CODES.includes(t.Code)
      )
      return service.isDOTSalive && !isSalivaPanelsAvailable
    },
    [serviceTests]
  )

  const disabledRegulatedUrineService = useCallback(
    (service: IDrugService) => {
      const isSalivaPanelAvailable = serviceTests?.[ServiceCode.DrugDOTOral].some((t) =>
        DOTSL_PANEL_CODES.includes(t.Code)
      )
      const isUrinePanelsAvailable =
        !isSalivaPanelAvailable ||
        serviceTests?.[ServiceCode.DrugDOTUrine].some((t) => DOTU_PANEL_CODES.includes(t.Code))

      return service.isDOTUrine && !isUrinePanelsAvailable
    },
    [serviceTests]
  )

  const disabledIfTestPresent = useCallback(
    (service: IDrugService) => {
      const code = service.FFServiceCode as ServiceCode
      return !!panelId && serviceTests?.[code].every((t) => t.PanelID !== panelId)
    },
    [panelId, serviceTests]
  )

  const disabledIfFlexPresent = useCallback(
    (service: IDrugService) => !service.isFlex && (serviceTests?.[ServiceCode.FLEX] ?? []).length > 0,
    [serviceTests]
  )

  const disabledMarketplaceService = useCallback(
    (service: IDrugService) => !!service.isMarketplace && notMarketplaceServicesCount > 0,
    [notMarketplaceServicesCount]
  )

  const disabledNotMarketplaceService = useCallback(
    (service: IDrugService) => !service.isMarketplace && marketplaceServicesCount > 0,
    [marketplaceServicesCount]
  )

  const checked = useCallback(
    (service: Service) => {
      return service.FFServiceCode === selectedServiceCode
    },
    [selectedServiceCode]
  )

  const disabled = useCallback(
    (service: IDrugService) => {
      return (
        !checked(service) &&
        (!service.isActive ||
          !service.hasTests ||
          !canAddTests(service) ||
          disabledIfTestPresent(service) ||
          disabledIfFlexPresent(service) ||
          disabledRegulatedSalivaService(service) ||
          disabledRegulatedUrineService(service) ||
          disabledMarketplaceService(service) ||
          disabledNotMarketplaceService(service))
      )
    },
    [
      checked,
      canAddTests,
      disabledIfTestPresent,
      disabledMarketplaceService,
      disabledNotMarketplaceService,
      disabledIfFlexPresent,
      disabledRegulatedSalivaService,
      disabledRegulatedUrineService,
    ]
  )

  useEffect(() => {
    const drugServices =
      orderableServices?.map((service) => {
        const serviceDrug = DrugService(
          service,
          serviceTests?.[service.FFServiceCode as ServiceCode] || [],
          orderableAccountServices
        )
        return {
          ...serviceDrug,
          disabled: disabled(serviceDrug),
          checked: checked(serviceDrug),
          onChange: (e: ChangeEvent<HTMLInputElement>) => onChangeTestType(e.target.value),
        }
      }) || []

    setServices(drugServices, (newServices) => {
      const testsForEnabledServices = newServices
        .filter((s) => !s.disabled)
        .reduce((acc: TestToBePerform[], service) => {
          return acc.concat(service.tests)
        }, [])
      onUpdateTests?.(testsForEnabledServices)
      setAllServicesDisabled(newServices.every((s) => s.disabled))
    })
  }, [
    orderableServices,
    orderableAccountServices,
    serviceTests,
    checked,
    disabled,
    onChangeTestType,
    setAllServicesDisabled,
    onUpdateTests,
    setServices,
  ])

  return (
    <Form.Group className={`${selectedServiceCode ? "section-border" : "invalid-section-border"} p-2`}>
      {services.map((item) => (
        <Form.Check
          label={item.ServiceDescription}
          id={`${item.FFServiceCode}-drug-test`}
          key={item.FFServiceCode}
          type="radio"
          name="serviceCode"
          className="font-weight-bold abc-radio"
          disabled={item.disabled}
          checked={item.checked}
          value={item.FFServiceCode}
          onChange={item.onChange}
        />
      ))}
    </Form.Group>
  )
}

export default DrugServicesPanel
